import { c as _c } from "react/compiler-runtime";
import { Drawer } from 'vaul';
import Image from 'next/image';
import type { ClothingItem, ClothingCategory } from '@/types/closet';
import { categoryConfig } from '@/data/closet';
import { Shimmer } from './Shimmer';
const SINGULAR_MAP: Record<string, string> = {
  TOPS: 'TOP',
  BOTTOMS: 'BOTTOM',
  ACCESSORIES: 'ACCESSORY'
};
function getSingularLabel(label: string): string {
  const upperLabel = label.toUpperCase();
  return SINGULAR_MAP[upperLabel] || upperLabel;
}
const EMPTY_PROCESSING_ITEMS: string[] = [];
interface CategoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  category: ClothingCategory | null;
  items: ClothingItem[];
  loading: boolean;
  onItemSelect: (item: ClothingItem) => void;
  selectedItem?: ClothingItem;
  processingItems?: string[]; // IDs of items currently being processed
}
export function CategoryDrawer(t0) {
  const $ = _c(24);
  const {
    isOpen,
    onClose,
    category,
    items,
    loading,
    onItemSelect,
    selectedItem,
    processingItems: t1
  } = t0;
  const processingItems = t1 === undefined ? EMPTY_PROCESSING_ITEMS : t1;
  if (!category) {
    return null;
  }
  const config = categoryConfig[category];
  let t2;
  if ($[0] !== processingItems) {
    t2 = new Set(processingItems);
    $[0] = processingItems;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const processingItemIds = t2;
  let t3;
  if ($[2] !== onClose) {
    t3 = open => !open && onClose();
    $[2] = onClose;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Drawer.Overlay className="fixed inset-0 z-50 bg-neutral-950/40" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mx-auto mt-4 h-1.5 w-12 flex-shrink-0 rounded-full bg-zinc-300 dark:bg-zinc-600" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== config.label) {
    t6 = getSingularLabel(config.label);
    $[6] = config.label;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex flex-col p-4"><Drawer.Title className="text-sm font-bold">SELECT {t6}</Drawer.Title></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== config.label || $[11] !== items || $[12] !== loading || $[13] !== onItemSelect || $[14] !== processingItemIds || $[15] !== selectedItem?.id) {
    t8 = <div className="px-4 pb-8">{loading ? <div className="flex h-32 items-center justify-center"><p className="text-xs text-zinc-600 dark:text-zinc-400">LOADING…</p></div> : items.length === 0 ? <div className="flex h-32 items-center justify-center"><p className="text-xs text-zinc-600 dark:text-zinc-400">NO {config.label.toUpperCase()} ITEMS IN YOUR CLOSET</p></div> : <div className="scrollbar-hide flex gap-4 overflow-x-auto pb-2">{items.map(item => {
          const isSelected = selectedItem?.id === item.id;
          const isProcessing = processingItemIds.has(item.id);
          return <button key={item.id} type="button" onClick={() => !isProcessing && onItemSelect(item)} disabled={isProcessing} className={`group relative size-32 flex-shrink-0 overflow-hidden rounded-lg transition-[opacity,box-shadow] duration-200 ${isProcessing ? "cursor-not-allowed opacity-50" : isSelected ? "ring-2 ring-zinc-900 dark:ring-white" : "hover:ring-2 hover:ring-zinc-300 dark:hover:ring-zinc-600"}`}><div className="relative size-full"><Image src={item.imageUrl} alt={item.name} fill={true} sizes="(max-width: 768px) 100vw, 50vw" className="object-contain" /></div>{isProcessing && <Shimmer />}<div className={`absolute inset-0 flex flex-col items-center justify-center bg-neutral-950/60 p-2 transition-opacity ${isProcessing ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>{isProcessing ? <p className="text-xs text-white">APPLYING</p> : <><p className="text-center text-xs font-medium text-white">{item.name.toUpperCase()}</p>{item.brand && <p className="mt-0.5 text-center text-xs text-white/80">{item.brand.toUpperCase()}</p>}</>}</div>{isSelected && <div className="absolute top-2 right-2 rounded-full bg-zinc-900 p-1 dark:bg-white"><svg className="size-3 text-white dark:text-zinc-900" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.71 5.29a1 1 0 010 1.41l-8 8a1 1 0 01-1.41 0l-4-4a1 1 0 11.41-1.41L8 12.59l7.29-7.29a1 1 0 11.41 0z" clipRule="evenodd" /></svg></div>}</button>;
        })}</div>}</div>;
    $[10] = config.label;
    $[11] = items;
    $[12] = loading;
    $[13] = onItemSelect;
    $[14] = processingItemIds;
    $[15] = selectedItem?.id;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <Drawer.Portal>{t4}<Drawer.Content className="closet-page fixed right-0 bottom-0 left-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] bg-white dark:bg-zinc-900">{t5}{t7}{t8}</Drawer.Content></Drawer.Portal>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isOpen || $[21] !== t3 || $[22] !== t9) {
    t10 = <Drawer.Root open={isOpen} onOpenChange={t3}>{t9}</Drawer.Root>;
    $[20] = isOpen;
    $[21] = t3;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}